import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Nav, NavLink, NavSubMenu, NavTag } from '~/shared-components';
import { Button } from '@bufferapp/ui';
import { profileChildTabPages, profileTabPages } from '~/routes';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line react/prop-types
const NavCounterTag = ({ labelName }) =>
  labelName != null && <NavTag type="counter" labelName={labelName} />;

const Tag = ({ showPaywallTag, labelName }) =>
  showPaywallTag ? (
    <NavTag type="paywall" />
  ) : (
    <NavCounterTag labelName={labelName} />
  );

Tag.propTypes = {
  showPaywallTag: PropTypes.bool.isRequired,
  labelName: PropTypes.string.isRequired,
};

const TabNavigation = ({
  profileNavTabs,
  profileId,
  draftsNeedApprovalCount,
  draftsCount,
  showNestedAnalyticsTab,
  showNestedSettingsTab,
  showReconnectButton,
  reconnectURL,
  showApprovalPaywallTag,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  return (
    profileId && (
      <Nav>
        {profileNavTabs.includes('queue') && (
          <NavLink to={profileTabPages.getRoute({ profileId, tabId: 'queue' })}>
            {t('tabs.queue')}
          </NavLink>
        )}
        {profileNavTabs.includes('stories') && (
          <NavLink
            to={profileTabPages.getRoute({ profileId, tabId: 'stories' })}
          >
            {t('tabs.stories')}
          </NavLink>
        )}
        {profileNavTabs.includes('pendingTiktok') && (
          <NavLink
            to={profileTabPages.getRoute({
              profileId,
              tabId: 'pendingTiktok',
            })}
          >
            {t('tabs.pendingTiktok')}
          </NavLink>
        )}
        {profileNavTabs.includes('pastReminders') && (
          <NavLink
            to={profileTabPages.getRoute({ profileId, tabId: 'pastReminders' })}
          >
            {t('tabs.pastReminders')}
          </NavLink>
        )}
        {profileNavTabs.includes('sentPosts') && (
          <NavLink
            to={profileTabPages.getRoute({ profileId, tabId: 'sentPosts' })}
            activeOnlyWhenExact={false}
            hasSubMenu
          >
            {t('tabs.sentPosts')}
          </NavLink>
        )}
        {showNestedAnalyticsTab && profileNavTabs.includes('overview') && (
          <NavSubMenu type="sentPosts">
            <NavLink
              to={profileTabPages.getRoute({ profileId, tabId: 'sentPosts' })}
              secondary
            >
              {t('tabs.listView')}
            </NavLink>
            <NavLink
              to={profileChildTabPages.getRoute({
                profileId,
                tabId: 'sentPosts',
                childTabId: 'overview',
              })}
              secondary
            >
              {t('tabs.overview')}
            </NavLink>
          </NavSubMenu>
        )}
        {profileNavTabs.includes('awaitingApproval') && (
          <NavLink
            to={profileTabPages.getRoute({
              profileId,
              tabId: 'awaitingApproval',
            })}
            testId="awaiting-approval-tab"
          >
            Approvals
            <Tag
              showPaywallTag={showApprovalPaywallTag}
              labelName={`${draftsNeedApprovalCount}`}
            />
          </NavLink>
        )}
        {profileNavTabs.includes('pendingApproval') && (
          <NavLink
            to={profileTabPages.getRoute({
              profileId,
              tabId: 'pendingApproval',
            })}
          >
            Approvals
            <Tag
              showPaywallTag={showApprovalPaywallTag}
              labelName={draftsNeedApprovalCount}
            />
          </NavLink>
        )}
        {profileNavTabs.includes('drafts') && (
          <NavLink
            to={profileTabPages.getRoute({ profileId, tabId: 'drafts' })}
          >
            <span data-testid="drafts-tab">
              {t('tabs.drafts')}
              <NavCounterTag labelName={draftsCount} />
            </span>
          </NavLink>
        )}
        {profileNavTabs.includes('grid') && (
          <NavLink to={profileTabPages.getRoute({ profileId, tabId: 'grid' })}>
            {t('tabs.shopGrid')}
          </NavLink>
        )}
        {profileNavTabs.includes('settings') && (
          <NavLink
            to={profileTabPages.getRoute({ profileId, tabId: 'settings' })}
            hasSubMenu
            activeOnlyWhenExact={false}
          >
            <span data-testid="settings-tab">{t('tabs.settings')}</span>
          </NavLink>
        )}
        {showNestedSettingsTab && (
          <NavSubMenu type="settings">
            <NavLink
              to={profileTabPages.getRoute({ profileId, tabId: 'settings' })}
              secondary
            >
              {t('tabs.general')}
            </NavLink>
            <NavLink
              to={profileChildTabPages.getRoute({
                profileId,
                tabId: 'settings',
                childTabId: 'postingSchedule',
              })}
              secondary
            >
              {t('tabs.postingSchedule')}
            </NavLink>
            {showReconnectButton && (
              <div style={{ display: 'inline-block' }}>
                <Button
                  type="secondary"
                  size="small"
                  label={loading ? t('tabs.reconnecting') : t('tabs.reconnect')}
                  onClick={(e) => {
                    e.preventDefault();
                    setLoading(true);
                    window.location.assign(reconnectURL);
                  }}
                />
              </div>
            )}
          </NavSubMenu>
        )}
      </Nav>
    )
  );
};

TabNavigation.defaultProps = {
  profileNavTabs: ['queue'],
  profileId: null,
  draftsNeedApprovalCount: 0,
  draftsCount: 0,
  showNestedAnalyticsTab: false,
  showNestedSettingsTab: false,
  showReconnectButton: false,
  showApprovalPaywallTag: false,
};

TabNavigation.propTypes = {
  profileNavTabs: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  draftsNeedApprovalCount: PropTypes.number,
  draftsCount: PropTypes.number,
  showNestedAnalyticsTab: PropTypes.bool,
  showNestedSettingsTab: PropTypes.bool,
  showReconnectButton: PropTypes.bool,
  reconnectURL: PropTypes.string.isRequired,
  showApprovalPaywallTag: PropTypes.bool,
};

export default TabNavigation;
